<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { blockUserAction } from '@/api/functions/users/block-user-action'
import { deleteUserAction } from '@/api/functions/users/delete-user-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const props = defineProps({
  user: { type: Object },
})

const emit = defineEmits(['confirmUser'])

const toast = useToast()
const router = useRouter()

const showDeleteModal = ref(false)
const showBlockModal = ref(false)
const userId = computed(() => props.user?.uuid)

// DELETE  USER
const deleteMutation = useMutationApi({
  mutationFn: (id: string) => deleteUserAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'کاربر با موفقیت حذف شد')
    emit('confirmUser')
    showDeleteModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleDeleteUser = () => {
  if (!userId.value) return
  deleteMutation.mutate(userId.value)
}

// BLOCK USER
const blockMutation = useMutationApi({
  mutationFn: (id: string) => blockUserAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'کاربر با موفقیت مسدود شد')
    emit('confirmUser')
    showBlockModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleBlockUser = () => {
  if (!userId.value) return
  blockMutation.mutate(userId.value)
}

const goToUserDetails = () => {
  router.push({
    path: `/users/${userId.value}`,
  })
}
</script>

<template>
    <div class="bg-white rounded-xl p-5 lg:p-8 lg:pb-6 relative flex flex-col-reverse lg:flex-col gap-6 lg:gap-0">

        <ConfirmModal v-model="showDeleteModal" title="حذف کاربر" :message="'آیا از حذف این کاربر اطمینان دارید؟'"
            confirm-text="بله حذف شود" cancel-text="بازگشت" @confirm="handleDeleteUser"
            @cancel="showDeleteModal = false" />

        <ConfirmModal v-model="showBlockModal" title="مسدودسازی کاربر"
            :message="'آیا از مسدودسازی این کاربر اطمینان دارید؟'" confirm-text="بله مسدود شود" cancel-text="بازگشت"
            @confirm="handleBlockUser" @cancel="showBlockModal = false" />

        <div class="relative lg:absolute bottom-0 lg:top-8 lg:end-8">
            <div class=" w-full lg:w-fit flex  items-center">
                <Button @click="goToUserDetails" :isAdmin="true"
                    class="w-1/2 lg:w-auto  rounded-[10px]! px-5 h-10! py-0 text-[13px] font-normal me-2">
                    مشاهده
                </Button>
                <Button @click="showDeleteModal = true"
                    class=" bg-transparent w-1/2 lg:w-auto border-[#2d5396] border-2 hover:bg-gray-100 rounded-[10px]! px-4 h-10! py-0 text-[13px] font-normal me-2">
                    <span class="flex items-center justify-center gap-2 text-[#2d5396]">
                        حذف کاربر
                    </span>
                </Button>
                <Button @click="showBlockModal = true"
                    class=" bg-transparent w-1/2 lg:w-auto border-[#2d5396] border-2 hover:bg-gray-100 rounded-[10px]! px-4 h-10! py-0 text-[13px] font-normal me-2">
                    <span class="flex items-center justify-center gap-2 text-[#2d5396]">
                        مسدودسازی کاربر
                    </span>
                </Button>
            </div>
        </div>

        <div class="flex flex-col gap-6  text-black text-sm font-medium">
            <!-- Header -->
            <div class="flex flex-col lg:flex-row items-center justify-center gap-2 lg:justify-start">
                <div
                    class="w-full lg:w-fit flex items-center justify-center text-[13px] bg-[#d5ddea] text-[#2d5396]  font-medium h-10 rounded-[10px] px-6">
                    کد کاربری
                    <span class="font-fa ms-1">{{ user?.id }}</span>
                    <!-- <span v-if="!isButtonHidden" class="mx-1">|</span> -->
                    <span>
                        {{ user?.status === 'VERIFIED' ? 'تایید شده' :
                            user?.status === 'EMAIL_VERIFICATION_PENDING' ? 'ثبت نام اولیه' :
                                user?.status === 'MOBILE_REGISTRATION_PENDING' ? ' در انتظار ثبت شماره موبایل' :
                                    user?.status === 'IDENTIFICATION_REGISTRATION_PENDING' ? ' در انتظار ارسال مدارک هویتی' :
                                        user?.status === 'IDENTIFICATION_VERIFICATION_PENDING' ? ' در انتظار تایید مدارک هویتی' :
                                            user?.status === 'MOBILE_VERIFICATION_PENDING' ? ' در انتظار تایید شماره موبایل' :
                                                'منقضی شده' }}
                    </span>
                </div>

            </div>
            <!-- Body -->
            <div class="flex flex-col gap-2.5 text-sm text-black font-medium mb-4">
                <div class="flex items-center gap-1">
                    <span>نام و نام خانوادگی:</span>

                    <span class="">{{ user?.fullName }}</span>
                </div>
                <div class="flex items-center gap-1">
                    <span>ایمیل:</span>
                    <span>{{ user?.email }}</span>
                </div>
            </div>

        </div>
    </div>

</template>
